import { getLexiconEntry } from '@tunasong/music-lib'
import { interpolateTokens } from '../theory-copy/interpolate-tokens.js'
import { useTheoryLevel } from '../theory-copy/use-theory-copy.js'

export interface VocalCopy {
  body: string
  tag: string
  displayName: string
  displayNameShort: string
}

/**
 * Resolve a `vocal` lexicon entry at the user's theory level, with its `{{tokens}}` filled.
 *
 * Two differences from `useTheoryCopy`, both deliberate:
 *
 *  1. **`theoryLevel: 'off'` floors to `beginner` rather than returning null.** Everywhere else
 *     in the suite, lexicon copy is a pedagogical *annotation* on a surface that still works
 *     without it — a chip on a chord, an explainer under a suggestion — so hiding it is the
 *     right response to "off". Here the copy IS the output: §12 gives one entry per verdict and
 *     forbids the UI composing a sentence of its own, so a null would leave a singer who just
 *     sang a phrase looking at a percentage and nothing else. "Off" means "don't teach me
 *     theory", not "don't tell me whether I sang the note".
 *  2. It interpolates, so no caller can forget the never-leak-raw-token guard.
 *
 * Returns null only for an id the lexicon does not carry — the same "render nothing rather than
 * a placeholder" contract every other surface has.
 */
export const useVocalCopy = (
  lexiconId: string,
  tokens: Record<string, string | number | undefined> = {}
): VocalCopy | null => {
  const level = useTheoryLevel()
  const entry = getLexiconEntry(lexiconId)
  if (!entry) {
    return null
  }
  const resolved = level === 'off' ? 'beginner' : level
  return {
    body: interpolateTokens(entry.copy[resolved], tokens),
    tag: interpolateTokens(entry.tagLabel[resolved], tokens),
    displayName: entry.displayName,
    displayNameShort: entry.displayNameShort,
  }
}
